import { useState, type FormEvent } from 'react';
import { Button } from '../../components/Button/Button';
import styles from './FamilyModal.module.css';

interface EditingFamily {
  id: number;
  name: string;
  email: string;
  status: 'active' | 'suspended' | 'invited';
  invite_token: string | null;
}

interface Props {
  editing: EditingFamily | null;
  pwLink: string;
  onClose(): void;
  onSave(input: { name: string; email: string }): void;
  onCopyLink(): void;
  onOpenLink(): void;
}

export function FamilyModal({ editing, pwLink, onClose, onSave, onCopyLink, onOpenLink }: Props) {
  const [name, setName] = useState(editing?.name ?? '');
  const [email, setEmail] = useState(editing?.email ?? '');

  const title = editing ? 'Rediger familie' : 'Opret familie';
  const sub = editing
    ? 'Ret familiens navn og login-e-mail.'
    : 'Opret en ny familie. Den bliver en selvstændig bruger med eget budget.';
  const cta = editing ? 'Gem ændringer' : 'Opret familie';

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    onSave({ name: name.trim(), email: email.trim() });
    if (!editing) return;
    onClose();
  }

  return (
    <div className={styles.overlay} onClick={onClose}>
      <form className={styles.card} onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className={styles.top}>
          <div>
            <h3 className={styles.title}>{title}</h3>
            <p className={styles.sub}>{sub}</p>
          </div>
          <button type="button" className={styles.close} onClick={onClose} aria-label="Luk">
            ×
          </button>
        </div>
        <div className={styles.form}>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="family-name">
              Familienavn
            </label>
            <input
              id="family-name"
              className={styles.input}
              placeholder="fx Familie Hansen"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="family-email">
              Login-e-mail
            </label>
            <input
              id="family-email"
              type="email"
              className={styles.input}
              placeholder="familie@example.dk"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <div className={styles.hint}>
              Familien logger ind med denne e-mail og får sit eget budget. Flere logins pr. familie
              kan tilføjes senere.
            </div>
          </div>
          {editing && editing.invite_token && (
            <div className={styles.linkSection}>
              <div className={styles.label}>Link til at oprette adgangskode</div>
              <div className={styles.hint}>
                Vi sender ingen mails automatisk. Kopiér linket og send det til familien — det
                indeholder en unik nøgle til netop denne konto.
              </div>
              <div className={styles.linkRow}>
                <input
                  className={styles.linkInput}
                  value={pwLink}
                  readOnly
                  onFocus={(e) => e.target.select()}
                />
                <button type="button" className={styles.copyBtn} onClick={onCopyLink}>
                  Kopiér
                </button>
              </div>
              <button type="button" className={styles.openPage} onClick={onOpenLink}>
                Åbn siden i ny fane →
              </button>
            </div>
          )}
          {editing && !editing.invite_token && editing.status !== 'invited' && (
            <div className={styles.linkSection}>
              <div className={styles.hint}>
                Familien har allerede oprettet en adgangskode. Der er ikke længere et
                invitationslink.
              </div>
            </div>
          )}
        </div>
        <div className={styles.actions}>
          <Button variant="secondary" type="button" onClick={onClose}>
            Annullér
          </Button>
          <Button variant="primary" type="submit">
            {cta}
          </Button>
        </div>
      </form>
    </div>
  );
}
